import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Input, InputNumber, Popconfirm, Radio, Row, Select } from 'antd';
import moment from 'moment';
import { DATE_FORMAT, formRules, requireText } from 'common/const/common.const';
import { ECommonFormType } from 'common/models/commonModel';
import { getTextFieldMinMaxLenght, useFormMapper } from 'common/helpers/form.helper';
import CustomDatePicker from 'common/components/DateTime/CustomDatePicker';
import { dateFormatterToUTC } from 'common/helpers/date.helper';
import { useIsContentRestricted } from 'common/hooks/useIsContentRestricted';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  setBitecoinRewardModel,
  setBitecoinRewardModelError,
  setBitecoinRewardModelParams,
} from 'app/store/reducers/bitecoin.reducer';
import {
  ERewardDiscountType,
  ERewardDiscountTypeText,
  ECommonEntityStatus,
  ECommonEntityStatusText,
  ERewardType,
  ERewardTypeText,
  IBitecoinRewardCreateParams,
  IBitecoinRewardUpdateParams,
} from 'entities/Bitecoin/Bitecoin.models';
import { BitecoinRewardCategorySelector } from 'entities/Bitecoin/components/BitecoinRewardCategorySelector';
import { MenuItemSelector } from 'entities/MenuItem/components/Selector/MenuItemSelector';

interface IComponentProps {
  createReward: (data: IBitecoinRewardCreateParams) => void;
  updateReward: (data: IBitecoinRewardUpdateParams) => void;
  deleteReward: () => void;
  typeForm: ECommonFormType;
}

export const BitecoinRewardForm: React.FC<IComponentProps> = ({
  createReward,
  updateReward,
  deleteReward,
  typeForm,
}) => {
  const dispatch = useAppDispatch();
  const { bitecoinRewardModel, bitecoinRewardModelLoading, bitecoinRewardModelParams, bitecoinRewardModelError } =
    useAppSelector((state) => state.bitecoin);
  const [rewardType, setRewardType] = useState<ERewardType>(ERewardType.Free);
  const [discountType, setDiscountType] = useState<ERewardDiscountType>(ERewardDiscountType.DiscountPercent);
  const { id: rewardId } = bitecoinRewardModel || {};
  const isCreateForm = typeForm === ECommonFormType.Add;
  const submitButtonText = isCreateForm ? 'Add' : 'Save';
  const canManageReward = !useIsContentRestricted();
  const isFieldDisabled = bitecoinRewardModelLoading || !canManageReward;
  const { fields, setField } = useFormMapper(
    [
      // 'type', 'status', 'discountType', 'category' and 'menuItem' we will set separately in useEffect
      // to have default preselected values
      'name',
      'priceInBitecoins',
      'discountValue',
    ],
    bitecoinRewardModel,
    bitecoinRewardModelParams,
    bitecoinRewardModelError
  );
  const { min, max } = useMemo(() => getTextFieldMinMaxLenght('name'), []);

  const mapRewardData = (values: any) => {
    const { name, category, menuItem, type, priceInBitecoins, status, discountType, discountValue, rewardDates } =
      values;
    const newData: IBitecoinRewardCreateParams = {
      name,
      categoryId: category.id,
      menuItemId: menuItem.id,
      type,
      priceInBitecoins,
      status,
      discountType,
    };

    if (type === ERewardType.Discounted) {
      newData.discountType = discountType;
      newData.discountValue = discountValue;
    }

    if (rewardDates) {
      if (rewardDates[0]) {
        newData.startDate = dateFormatterToUTC(rewardDates[0]);
      }
      if (rewardDates[1]) {
        newData.endDate = dateFormatterToUTC(rewardDates[1]);
      }
    }

    return newData;
  };

  const addReward = (values: any) => {
    const rewardData = mapRewardData(values);

    createReward(rewardData);
  };

  const editReward = (values: any) => {
    if (rewardId) {
      const rewardData = mapRewardData(values);

      updateReward(rewardData);
    }
  };

  const onCancel = () => {
    dispatch(setBitecoinRewardModel(null));
    dispatch(setBitecoinRewardModelError(null));
    dispatch(setBitecoinRewardModelParams(null));

    history.go(-1);
  };

  useEffect(() => {
    if (!bitecoinRewardModelLoading && bitecoinRewardModel) {
      const { status, type, discountType, category, menuItem, startDate, endDate } = bitecoinRewardModel;

      setField({ name: 'status', value: status });

      setField({ name: 'type', value: type });
      setRewardType(type as ERewardType);

      setField({ name: 'discountType', value: discountType });
      setDiscountType(discountType as ERewardDiscountType);

      setField({ name: 'category', value: category });
      setField({ name: 'menuItem', value: menuItem });

      const rewardDatesValue = [];

      if (startDate) {
        rewardDatesValue[0] = moment(startDate);
      }

      if (endDate) {
        rewardDatesValue[1] = moment(endDate);
      }

      setField({ name: 'rewardDates', value: rewardDatesValue });
    }
  }, [bitecoinRewardModelLoading, bitecoinRewardModel]);

  return (
    <Form
      disabled={isFieldDisabled}
      className="mt-5"
      onFinish={isCreateForm ? addReward : editReward}
      fields={fields}
      layout="vertical"
    >
      <span className="basic__text_title" title="General info">
        General info
      </span>
      <Row gutter={[28, 0]} className="mt-5">
        <Col span={12}>
          <Form.Item rules={[formRules.required]} name="category" label="Reward Category">
            <BitecoinRewardCategorySelector placeholder="Select reward category" disabled={!isCreateForm || isFieldDisabled} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item rules={[formRules.required, min, max]} name="name" label="Reward name">
            <Input placeholder="Specify reward name" name="name" type="text" disabled={!isCreateForm || isFieldDisabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[28, 0]}>
        <Col span={12}>
          <Form.Item
            rules={[
              {
                required: true,
                message: requireText,
              },
            ]}
            label="Menu item"
            name="menuItem"
          >
            <MenuItemSelector placeholder="Select menu item" disabled={!isCreateForm || isFieldDisabled} />
          </Form.Item>
        </Col>
      </Row>

      <span className="basic__text_title" title="Redemption criteria">
        Reward settings
      </span>
      <Row gutter={[28, 0]} className="mt-5">
        <Col span={12}>
          <Form.Item
            rules={[
              {
                required: true,
                message: requireText,
              },
            ]}
            label="Reward type"
            name="type"
            initialValue={ERewardType.Free}
          >
            <Radio.Group onChange={(e) => setRewardType(e.target.value)} disabled={!isCreateForm || isFieldDisabled} className="flex">
              <Radio value={ERewardType.Free}>{ERewardTypeText.Free}</Radio>
              <Radio value={ERewardType.Discounted}>{ERewardTypeText.Discounted}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      {rewardType === ERewardType.Discounted && (
        <Row gutter={[28, 0]}>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: requireText,
                },
              ]}
              label="Discount type"
              name="discountType"
              initialValue={ERewardDiscountType.DiscountPercent}
            >
              <Select className="width-full" onChange={(value) => setDiscountType(value)} disabled={!isCreateForm || isFieldDisabled}>
                <Select.Option
                  value={ERewardDiscountType.DiscountPercent}
                  label={ERewardDiscountTypeText.DiscountPercent}
                >
                  {ERewardDiscountTypeText.DiscountPercent}
                </Select.Option>
                <Select.Option value={ERewardDiscountType.DiscountFix} label={ERewardDiscountTypeText.DiscountFix}>
                  {ERewardDiscountTypeText.DiscountFix}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: requireText,
                },
                {
                  type: 'number',
                  min: 1,
                  max: 1000,
                  message: 'Price value must be equal to 1-1000',
                },
              ]}
              label={discountType === ERewardDiscountType.DiscountPercent ? '% off discount' : 'Amount of peso'}
              name="discountValue"
            >
              <InputNumber
                type="number"
                name="discountValue"
                max={1000}
                min={1}
                precision={0}
                placeholder={
                  discountType === ERewardDiscountType.DiscountPercent
                    ? 'Specify % off discount'
                    : 'Specify the amount of peso'
                }
                disabled={!isCreateForm || isFieldDisabled}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={[28, 0]}>
        <Col span={12}>
          <Form.Item
            rules={[
              {
                required: true,
                message: requireText,
              },
              {
                type: 'number',
                min: 1,
                max: 1000,
                message: 'Price value must be equal to 1-1000',
              },
            ]}
            label="Bitecoins required"
            name="priceInBitecoins"
          >
            <InputNumber
              type="number"
              name="priceInBitecoins"
              max={1000}
              min={1}
              precision={0}
              placeholder="Specify number of bitecoins"
              disabled={!isCreateForm || isFieldDisabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[28, 0]}>
        <Col span={12}>
          <Form.Item
            rules={[
              {
                required: true,
                message: requireText,
              },
            ]}
            label="Reward Status"
            name="status"
            initialValue={ECommonEntityStatus.Inactive}
          >
            <Select
              className="width-full"
              disabled={(!isCreateForm && bitecoinRewardModel?.status === ECommonEntityStatus.Archived) || isFieldDisabled}
            >
              <Select.Option value={ECommonEntityStatus.Active} label={ECommonEntityStatusText.Active}>
                {ECommonEntityStatusText.Active}
              </Select.Option>
              <Select.Option value={ECommonEntityStatus.Inactive} label={ECommonEntityStatusText.Inactive}>
                {ECommonEntityStatusText.Inactive}
              </Select.Option>
              <Select.Option value={ECommonEntityStatus.Archived} label={ECommonEntityStatusText.Archived}>
                {ECommonEntityStatusText.Archived}
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <span className="basic__text_title" title="Redemption criteria">
        For seasonal rewards (optional)
      </span>
      <Row gutter={[28, 0]} className="mt-5">
        <Col span={12}>
          <Form.Item name="rewardDates" label="Availability period">
            <CustomDatePicker.RangePicker
              className="width-full"
              disabledDate={(current) => current && current < moment().startOf('day')}
              getPopupContainer={(triggerNode) => triggerNode as HTMLElement}
              format={DATE_FORMAT}
              showTime={false}
              allowEmpty={[false, true]}
              disabled={!isCreateForm || isFieldDisabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row justify="space-between" align="middle">
        <Col span={16}>
          <Form.Item className="mb-0">
            <Button
              className="mr-8"
              htmlType="submit"
              type="primary"
              loading={bitecoinRewardModelLoading}
              title={submitButtonText}
              disabled={
                (!isCreateForm && bitecoinRewardModel?.status === ECommonEntityStatus.Archived) || !canManageReward
              }
            >
              {submitButtonText}
            </Button>
            <Button onClick={onCancel} loading={bitecoinRewardModelLoading} title="Cancel">
              Cancel
            </Button>
          </Form.Item>
        </Col>
        {!isCreateForm && (
          <Popconfirm
            disabled={bitecoinRewardModelLoading || !canManageReward}
            title="Are you sure you want to delete the reward?"
            okType="danger"
            onConfirm={deleteReward}
            okText="Delete"
          >
            <Button
              danger
              disabled={bitecoinRewardModelLoading || !canManageReward}
              loading={bitecoinRewardModelLoading}
              title="Delete"
            >
              Delete
            </Button>
          </Popconfirm>
        )}
      </Row>
    </Form>
  );
};
